import { translateLabel } from "@/i18n/messages";
import React, { useState } from "react";
import { FlagIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  emptyReportForm,
  normalizeReportInput,
  REPORT_CATEGORIES,
  submitReport,
  validateReportInput,
} from "@/lib/report";

export function ReportContent({ targetType, targetId, targetName, compact = false }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyReportForm());
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const input = normalizeReportInput({ targetType, targetId, form });
    const validation = validateReportInput(input);
    if (validation) {
      toast.error(validation);
      return;
    }

    setSaving(true);
    try {
      await submitReport(input);
      toast.success(translateLabel("Report submitted"), { description: translateLabel("It will be reviewed before any content is changed.") });
      setForm(emptyReportForm());
      setOpen(false);
    } catch (error) {
      toast.error(error.message || translateLabel("Could not submit report"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {compact ? (
        <DialogTrigger asChild>
          <button
            type="button"
            data-testid="report-toggle"
            className="flex items-center gap-1.5 text-xs font-bold text-zinc-500 hover:text-white"
          >
            <FlagIcon size={14} /> Report
          </button>
        </DialogTrigger>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[#121214] p-5" data-testid="report-content">
          <DialogTrigger asChild>
            <button
              type="button"
              data-testid="report-toggle"
              className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm font-bold text-zinc-200 hover:bg-white/10"
            >
              <FlagIcon size={16} /> {targetName ? `Report ${targetName}` : "Report"}
            </button>
          </DialogTrigger>
        </div>
      )}

      <DialogContent className="z-[1400] max-h-[90dvh] max-w-2xl overflow-y-auto border-white/10 bg-[#121214] text-white">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold uppercase">{translateLabel("Report a problem")}</DialogTitle>
          <DialogDescription className="text-zinc-400">
            Report an issue with {targetName || "this content"}. Nothing is hidden or changed automatically.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="mt-2 space-y-4" data-testid="report-form">
          <label className="block">
            <span className="label-tiny">{translateLabel("Reason")}</span>
            <select
              value={form.category}
              onChange={(event) => setForm((current) => ({ ...current, category: event.target.value }))}
              className="mt-2 w-full rounded-xl border border-white/10 bg-[#18181b] px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
              data-testid="report-category"
            >
              {REPORT_CATEGORIES.map((entry) => <option key={entry.value} value={entry.value}>{translateLabel(entry.label)}</option>)}
            </select>
          </label>

          <label className="block">
            <span className="label-tiny">{translateLabel("Details · optional")}</span>
            <textarea
              rows={3}
              maxLength={3000}
              value={form.details}
              onChange={(event) => setForm((current) => ({ ...current, details: event.target.value }))}
              placeholder={translateLabel("Add context that will help moderation.")}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-zinc-600 focus:border-[#CCFF00] focus:outline-none"
              data-testid="report-details"
            />
          </label>

          <div className="grid gap-3 md:grid-cols-2">
            <label>
              <span className="label-tiny">{translateLabel("Your name · optional")}</span>
              <input
                value={form.contactName}
                onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
            <label>
              <span className="label-tiny">{translateLabel("Email · optional")}</span>
              <input
                type="email"
                value={form.contactEmail}
                onChange={(event) => setForm((current) => ({ ...current, contactEmail: event.target.value }))}
                className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-[#CCFF00] focus:outline-none"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={saving}
            data-testid="report-submit"
            className="rounded-full bg-[#CCFF00] px-5 py-2.5 text-sm font-bold text-black disabled:opacity-50"
          >
            {saving ? "Submitting…" : "Submit report"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
